import React, { useEffect, useState, useContext } from "react";
import "../styles/global.css";
import { useNavigate } from "react-router-dom";
import { MenuDrawer } from "../screens/_menuDrawer";
import { getAuth } from "firebase/auth";
import countCartItems from "../functions/countCartItems";
import { useFirestore } from "../firebase/FirestoreContext";
import { collectionPath, EnvContext, environment } from "../context/EnvContext";
import Tooltip from "@mui/material/Tooltip";
import EnvironmentStatus from "./EnvironmentStatus";
import { PersonOutlineOutlined } from "@mui/icons-material";
import { useModal } from "../context/ModalContext"; // Import the ModalContext hook
import SearchAutocomplete from "./SearchAutocomplete";

import styled from "@emotion/styled";

import { logAnalyticsEvent } from "../firebase/firebaseConfig";
import HeaderBanner from "./HeaderBanner";
import { css } from "@emotion/react";
import HeaderContext from "../context/HeaderContext";

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 1000;
  max-width: 1120px;
  margin: 0 auto;
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(25px);
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  transition: transform 0.3s ease-in-out;
  transform: translateY(0px);

  @keyframes backgroundTransition {
    0% {
      background-color: transparent;
      backdrop-filter: blur(0px);
    }
    100% {
      background-color: black;
      backdrop-filter: blur(25px);
    }
  }

  @keyframes backgroundTransitionReverse {
    0% {
      background-color: rgba(0, 0, 0, 0.9);
      backdrop-filter: blur(25px);
    }
    100% {
      background-color: transparent;
      backdrop-filter: blur(0px);
    }
  }

  ${({ $theme }) =>
    $theme === "light" &&
    css`
      background-color: transparent;
      backdrop-filter: none;
      animation: backgroundTransitionReverse 0.5s ease-in-out;
    `}

  ${({ $isHidable }) =>
    $isHidable &&
    css`
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    `}

  ${({ $isHidable, $theme }) =>
    $isHidable &&
    $theme === "light" &&
    css`
      background-color: rgba(0, 0, 0, 0.9);
      backdrop-filter: blur(25px);
      animation: backgroundTransition 0.5s ease-in-out;
    `}

  ${({ $isHidden }) =>
    $isHidden &&
    css`
      transform: translateY(-60px);
    `}
  
  @media (min-width: 892px) {
    padding: 5px 0;
  }
`;

const CartCountIcon = styled.i`
  align-items: center;
  background-color: #017bff;
  border-radius: 50%;
  box-shadow: 0px 3px 5px rgba(0;0;0;.1);
  color: white;
  display: flex;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  height: 18px;
  justify-content: center;
  position: absolute;
  right: 0px;
  top: 0px;
  width: 18px;

  @media (min-width: 892px) {
    top: 5px;
  }

  ${({ $pulse }) =>
    $pulse &&
    css`
      transition: transform 0.2s ease;
      animation: cartPulse 0.6s ease-out;
    `}

  @keyframes cartPulse {
    0% {
      background-color: #017bff;
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.8;
    }
    60% {
      transform: scale(1);
      background-color: black;
      opacity: 0.8;
    }
    70% {
      transform: scale(1.1);
      opacity: 0.8;
    }
    100% {
      background-color: #017bff;
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const breakpoints = [576, 650, 760, 892, 1024];
const mq = breakpoints.map((bp) => `@media (min-width: ${bp}px)`);

const Header = ({ search = true, theme = "dark", products }) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const { isHidden, hasScrolledFromTop, isPulsing } = useContext(HeaderContext);

  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  const firestore = useFirestore();
  const { setCurrentModal } = useModal();

  useEffect(() => {
    window.feather.replace();
  }, [isMenuOpen]);

  useEffect(() => {
    let unsubscribe;

    if (user?.uid) {
      unsubscribe = countCartItems(
        firestore,
        user.uid,
        setCartCount,
        collectionPath,
      );
    } else {
      setCartCount(0); // Set count to 0 if no user is logged in
    }
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [firestore, user]);

  const toggleMenu = () => {
    logAnalyticsEvent("select_content", {
      content_type: "menu",
      item_id: "menu",
      environment: environment,
    });
    setMenuOpen((prev) => !prev);
  };

  const handleProfileClick = () => {
    if (user?.email) {
      navigate("/useraccount");
    } else {
      setCurrentModal("signUp");
    }
  };

  return (
    <>
      <EnvironmentStatus />
      <HeaderBanner />
      <HeaderContainer
        $isHidden={isHidden}
        $theme={theme}
        $isHidable={hasScrolledFromTop}
      >
        {/** HEADER */}
        <div css={{ ...styles.header.container }}>
          {/** HEADER FIRST BLOCK*/}
          <div css={{ ...styles.menu.container }} onClick={toggleMenu}>
            <i
              css={{ ...styles.icon }}
              data-feather={isMenuOpen ? "x" : "menu"}
              style={{ color: theme === "dark" ? "black" : "white" }}
            />
          </div>
          {/** HEADER SECOND BLOCK*/}
          <div css={{ ...styles.logo.container }} onClick={() => navigate("/")}>
            <img
              src={
                theme === "dark"
                  ? "/images/fullproof_logo_black.svg"
                  : "/images/fullproof_logo_white.svg"
              }
              alt="Fullproof"
              css={{ ...styles.logo.image }}
            />
          </div>
          {/** HEADER THIRD BLOCK*/}
          <div css={{ ...styles.cart.container }}>
            {/** PROFILE ICON */}
            <PersonOutlineOutlined
              focusable
              fontSize="inherit"
              onClick={handleProfileClick}
              style={{
                cursor: "pointer",
                color: theme === "dark" ? "black" : "white",
              }}
            />
            {/** CART ICON */}
            <Tooltip
              title={
                user && user.uid
                  ? ""
                  : "Start shopping or create an account to add items to your cart"
              }
              placement="left-start"
              disableHoverListener={!!(user && user.uid)}
            >
              <div
                style={{
                  opacity: user && user.uid ? 1 : 0.5,
                  cursor: user && user.uid ? "pointer" : "none",
                  lineHeight: 0,
                }}
                onClick={
                  user && user.uid
                    ? () => {
                        // Track the cart click event
                        logAnalyticsEvent("select_content", {
                          content_type: "cart",
                          item_id: "cart_page",
                          environment: environment,
                        });

                        // Navigate to the cart page
                        navigate("/cart");
                      }
                    : null
                }
              >
                <i
                  data-feather={"shopping-bag"}
                  css={{ ...styles.cart.icon }}
                  style={{ color: theme === "dark" ? "black" : "white" }}
                />
                {cartCount > 0 && (
                  <CartCountIcon $pulse={isPulsing}>
                    {user && user.uid ? cartCount : 0}
                  </CartCountIcon>
                )}
                {/* Display current number of items in cart */}
              </div>
            </Tooltip>
          </div>
        </div>
      </HeaderContainer>

      <MenuDrawer isOpen={isMenuOpen} toggleMenu={toggleMenu} />
    </>
  );
};

// Styles for HeaderShop component (unchanged)
const styles = {
  header: {
    container: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "0 7px",
    },
  },
  menu: {
    container: {
      flex: 1,
      cursor: "pointer",
      display: "flex",
    },
  },
  icon: {
    width: "28px",
    height: "28px",
  },
  cart: {
    container: {
      flex: 1,
      display: "flex",
      gap: "5px",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-end",
      fontSize: "30px",
    },
    icon: {
      width: "24px",
      height: "24px",
    },
  },
  logo: {
    container: {
      flex: 2.5,
      cursor: "pointer",
      display: "flex",
    },
    image: {
      height: "auto",
      maxHeight: 36,
      width: "100%",
      marginTop: 5,
      [mq[0]]: {
        maxHeight: 42,
        marginTop: 6,
      },
    },
  },
};

export default Header;
